import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, RefreshCw, Save, Clock, History, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS = [
  { value: 'escala', label: 'Seguir a escala' },
  { value: 'normal', label: 'Normal (dia de trabalho)' },
  { value: 'dsr', label: 'DSR' },
  { value: 'folga', label: 'Folga' },
  { value: 'compensado', label: 'Compensado' },
  { value: 'feriado', label: 'Feriado' },
];
const labelTipo = (v) => (TIPOS.find((t) => t.value === (v || 'escala')) || {}).label || v;

const formatDataBR = (iso) => {
  if (!iso) return '';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};
const formatDataHoraBR = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
};

const HistoricoAjustes = ({ historico }) => {
  if (!historico?.length) return null;
  return (
    <div className="space-y-2" data-testid="apuracao-ajuste-historico">
      <p className="text-sm font-medium flex items-center gap-1.5"><History className="w-4 h-4" /> Histórico de ajustes</p>
      <div className="max-h-40 overflow-auto border rounded-md divide-y text-xs">
        {[...historico].reverse().map((h, i) => (
          <div key={i} className="p-2" data-testid={`apuracao-ajuste-historico-item-${i}`}>
            <p className="font-medium">{formatDataHoraBR(h.em)} — {h.por_nome || '-'}</p>
            <p className="text-slate-600">
              Tipo: {labelTipo(h.anterior?.tipo_dia)} → {labelTipo(h.novo?.tipo_dia)} | Abono: {h.anterior?.abonar_falta ? 'Sim' : 'Não'} → {h.novo?.abonar_falta ? 'Sim' : 'Não'}
            </p>
            {h.novo?.observacao && <p className="text-slate-500">Obs.: {h.novo.observacao}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

const ModalEditarApuracaoDia = ({ open, onClose, empregado, jornada, periodo, token, onSaved, onEditarMarcacoes }) => {
  const [form, setForm] = useState({ tipo_dia: 'escala', abonar_falta: false, observacao: '' });
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(null);
  const [confirmPeriodo, setConfirmPeriodo] = useState(false);

  useEffect(() => {
    if (!open || !empregado?.id || !jornada?.data) return;
    setConfirmPeriodo(false);
    setLoading(true);
    fetch(`${API_URL}/api/apuracao/ajuste-dia?empregado_id=${empregado.id}&data=${jornada.data}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        setForm({ tipo_dia: d.tipo_dia || 'escala', abonar_falta: !!d.abonar_falta, observacao: d.observacao || '' });
        setHistorico(d.historico || []);
      })
      .catch(() => toast.error('Erro ao carregar ajuste do dia'))
      .finally(() => setLoading(false));
  }, [open, empregado, jornada, token]);

  const salvar = async (recalcular) => {
    setSalvando(recalcular);
    try {
      const res = await fetch(`${API_URL}/api/apuracao/ajuste-dia`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          empregado_id: empregado.id,
          data: jornada.data,
          tipo_dia: form.tipo_dia === 'escala' ? null : form.tipo_dia,
          abonar_falta: form.abonar_falta,
          observacao: form.observacao,
          recalcular,
          data_inicio: periodo?.inicio,
          data_fim: periodo?.fim,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Erro ao salvar ajuste');
      toast.success(data.message);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSalvando(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="apuracao-editar-dia-modal">
        <DialogHeader>
          <DialogTitle>Editar apuração — {formatDataBR(jornada?.data)}</DialogTitle>
          <DialogDescription>{empregado?.nome}. O ajuste manual prevalece sobre a escala nos próximos recálculos.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Tipo do dia</Label>
              <Select value={form.tipo_dia} onValueChange={(v) => setForm({ ...form, tipo_dia: v })}>
                <SelectTrigger data-testid="apuracao-tipo-dia-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`apuracao-tipo-dia-opcao-${t.value}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Atual na apuração: {jornada?.tipo_dia || 'normal'}</p>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.abonar_falta} onCheckedChange={(v) => setForm({ ...form, abonar_falta: !!v })} data-testid="apuracao-abonar-falta-checkbox" />
              Abonar / retirar a falta do dia
            </label>
            <div className="space-y-1.5">
              <Label>Observação / justificativa</Label>
              <Textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} rows={2} data-testid="apuracao-observacao-input" />
            </div>
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onEditarMarcacoes} data-testid="apuracao-editar-marcacoes-btn">
              <Clock className="w-4 h-4" /> Editar marcações do dia
            </Button>
            <HistoricoAjustes historico={historico} />
            {confirmPeriodo && (
              <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800" data-testid="apuracao-confirmar-periodo-alerta">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                Todos os dias de {formatDataBR(periodo?.inicio)} a {formatDataBR(periodo?.fim)} serão recalculados. Clique novamente para confirmar.
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} data-testid="apuracao-cancelar-btn">Cancelar</Button>
          <Button variant="secondary" disabled={!!salvando || loading} className="gap-1.5" data-testid="apuracao-salvar-periodo-btn"
            onClick={() => (confirmPeriodo ? salvar('periodo') : setConfirmPeriodo(true))}>
            {salvando === 'periodo' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {confirmPeriodo ? 'Confirmar: recalcular período' : 'Salvar e recalcular período'}
          </Button>
          <Button disabled={!!salvando || loading} className="gap-1.5" onClick={() => salvar('dia')} data-testid="apuracao-salvar-dia-btn">
            {salvando === 'dia' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar e recalcular dia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalEditarApuracaoDia;
